import { useCallback, useEffect, useState } from "react";

import { toError, type AsyncState } from "@/application/hooks/async-state";
import type { Vehicle } from "@/domain/models/vehicle";
import { repositories } from "@/infrastructure/repositories";

export type { AsyncState };

export function useVehicles() {
  const [state, setState] = useState<AsyncState<Vehicle[]>>({
    status: "loading",
    data: null,
    error: null,
  });

  const reload = useCallback(async () => {
    setState({ status: "loading", data: null, error: null });
    try {
      const data = await repositories.vehicles.list();
      if (data.length === 0) {
        setState({ status: "empty", data: null, error: null });
        return;
      }
      setState({ status: "success", data, error: null });
    } catch (error) {
      setState({ status: "error", data: null, error: toError(error) });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const remove = useCallback(
    async (id: string) => {
      await repositories.vehicles.remove(id);
      await reload();
    },
    [reload],
  );

  const removeMany = useCallback(
    async (ids: string[]) => {
      await Promise.all(ids.map((id) => repositories.vehicles.remove(id)));
      await reload();
    },
    [reload],
  );

  const duplicate = useCallback(
    async (vehicle: Vehicle) => {
      const { id: _id, ...input } = vehicle;
      await repositories.vehicles.create({
        ...input,
        gallery: [...input.gallery],
        features: [...input.features],
      });
      await reload();
    },
    [reload],
  );

  return { ...state, reload, remove, removeMany, duplicate };
}
